<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import * as Sentry from '@sentry/vue';
import FormSelect from 'v3/components/Form/Select.vue';

const props = defineProps({
  value: {
    type: String,
    required: true,
    validator: value =>
      [
        'division',
        'ding',
        'bell',
        'chime',
        'soft_chime',
        'magic',
        'ping',
      ].includes(value),
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);

const ALERT_TONES = Object.freeze([
  {
    value: 'division',
    label: 'Division',
    asset: 'division',
    volume: 1,
  },
  {
    value: 'ding',
    label: 'Ding',
    asset: 'ding',
    volume: 1,
  },
  {
    value: 'bell',
    label: 'Bell',
    asset: 'bell',
    volume: 1,
  },
  {
    value: 'chime',
    label: 'Chime',
    asset: 'chime',
    volume: 1,
  },
  {
    value: 'soft_chime',
    label: 'Soft chime',
    asset: 'chime',
    volume: 0.35,
  },
  {
    value: 'magic',
    label: 'Magic',
    asset: 'magic',
    volume: 1,
  },
  {
    value: 'ping',
    label: 'Ping',
    asset: 'ping',
    volume: 1,
  },
]);

const alertTones = computed(() =>
  ALERT_TONES.map(({ value, label }) => ({
    value,
    label,
  }))
);

const selectedValue = computed({
  get: () => props.value,
  set: value => {
    emit('change', value);
  },
});

const selectedToneConfig = computed(
  () =>
    ALERT_TONES.find(tone => tone.value === selectedValue.value) ||
    ALERT_TONES[0]
);

const audio = new Audio();

const playAudio = async () => {
  try {
    audio.pause();
    audio.currentTime = 0;
    audio.volume = selectedToneConfig.value.volume;
    audio.src = `/audio/dashboard/${selectedToneConfig.value.asset}.mp3`;
    await audio.play();
  } catch (error) {
    Sentry.captureException(error);
  }
};
</script>

<template>
  <div class="flex items-center gap-2">
    <FormSelect
      v-model="selectedValue"
      name="alertTone"
      spacing="compact"
      class="flex-grow"
      :value="selectedValue"
      :options="alertTones"
      :label="label"
    >
      <option
        v-for="tone in alertTones"
        :key="tone.label"
        :value="tone.value"
        :selected="tone.value === selectedValue"
      >
        {{ tone.label }}
      </option>
    </FormSelect>
    <button
      v-tooltip.top="
        $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.PLAY')
      "
      class="border-0 shadow-sm outline-none flex justify-center items-center size-10 appearance-none rounded-xl ring-n-weak ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-n-brand flex-shrink-0 mt-[1.75rem]"
      @click="playAudio"
    >
      <Icon icon="i-lucide-volume-2" />
    </button>
  </div>
</template>
